import requestWrapper from './requestWrapper';
import { client, fileClient, growthClient, noProxyClient } from './requesters';

import ErrorRMW from '../model/ErrorRMW';
import Constants from '../constants';
import SelectPropertyConstants from '../constants/SelectPropertyConstants';
import queryUtils from './queryUtils';
import {
  RENTAL_GROWTH_API_V1_PREFIX,
  RENTAL_MANAGER_API_V1_PREFIX,
  RENTAL_MANAGER_API_V2_PREFIX,
  RENTAL_MANAGER_API_WEB_V1_PREFIX,
} from '../constants/RouteConstants';

import appName from '../../appName';

const { GET, POST, PUT } = Constants;

const api = {
  waitlist: {
    startWaitlist(listingAlias, customerId) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/waitlist/setWaitlist`;
      return requestWrapper(client, GET, endpoint, {
        params: {
          aliasEncoded: listingAlias,
          customerId,
        },
      });
    },
    endWaitlist(listingAlias, customerId) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/waitlist/removeWaitlist`;
      return requestWrapper(client, GET, endpoint, {
        params: {
          aliasEncoded: listingAlias,
          customerId,
        },
      });
    },
  },
  claims: {
    getExclusivePropertiesForAllUnverified() {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/getExclusivePropertiesForAllUnverified`;
      return requestWrapper(client, GET, endpoint);
    },
    deleteClaimMethod(type, value) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/delete`;
      return requestWrapper(client, GET, endpoint, {
        params: {
          type,
          value,
        },
      });
    },
    deleteAllUnverifiedClaimMethods() {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/deleteAllUnverified`;
      return requestWrapper(client, GET, endpoint);
    },
    getClaimMethods() {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/list`;
      return requestWrapper(client, GET, endpoint);
    },
    addContactMethod(type, value) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/add`;
      return requestWrapper(client, GET, endpoint, {
        params: {
          type,
          value,
        },
      });
    },
    propertyLookup(type, value) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/propertyLookup`;
      return requestWrapper(client, GET, endpoint, {
        params: {
          type,
          value,
        },
      });
    },
    propertyLookupAll({ includeVerified = true, includeUnverified = true }) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/propertyLookupAll`;
      return requestWrapper(client, GET, endpoint, {
        params: {
          includeVerified,
          includeUnverified,
        },
      });
    },
    getExclusiveProperties(type, value) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/getExclusiveProperties`;
      return requestWrapper(client, GET, endpoint, {
        params: {
          type,
          value,
        },
      });
    },
  },
  multibrandSupport: {
    appConfig(params) {
      const { appName = 'zrm_web' } = params;
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/multibrandSupport/appConfig`;
      return requestWrapper(client, GET, endpoint, { params: { appName } });
    },
  },
  support: {
    submitRequest(body) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/support/submitRequest`;
      return requestWrapper(client, POST, endpoint, { data: body });
    },
    getHelpTopics() {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/support/getHelpTopics`;
      return requestWrapper(client, GET, endpoint);
    },
  },
  properties: {
    multi(bodyParams) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/multi`;

      return requestWrapper(client, POST, endpoint, {
        data: JSON.stringify(bodyParams),
        timeout: 5000,
      });
    },
    address: {
      rentZestimate(fullAddress, unit = null) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/properties/addresses/rentZestimate`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            fullAddress,
            unit,
          },
        });
      },
      details(addressData) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/properties/addresses/details`;

        const params = { fullAddress: addressData.address };
        if (addressData.unit) {
          params.unit = addressData.unit;
        }

        return requestWrapper(client, GET, endpoint, { params });
      },
      verification(addressData) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/properties/addresses/verification`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            fullAddress: addressData.address,
            unit: addressData.unit,
            propertyTypeCode: addressData.propertyTypeCode,
          },
        });
      },
      updateAddress(data) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/address`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyId: data.propertyId,
            fullAddress: data.fullAddress,
            unitNumber: data.unitNumber,
            propertyTypeCode: data.propertyTypeCode,
          },
        });
      },
    },

    locations: {
      getRestrictions(data) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/locations/restrictions`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            fullAddress: data.address,
            unit: data.unit,
            listingTypeCode: data.listingTypeCode,
            propertyTypeCode: data.propertyTypeCode,
            listingAlias: data.listingAlias,
          },
        });
      },
      getPublicRestrictions(data) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/properties/locations/restrictions`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            fullAddress: data.address,
            unit: data.unit,
            listingTypeCode: data.listingTypeCode,
            propertyTypeCode: data.propertyTypeCode,
            listingAlias: data.listingAlias,
          },
        });
      },
    },
    rentGuarantee: {
      saveExperimentDetails({ variation, details, listingAlias }) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/saveExperimentDetails`;

        return requestWrapper(client, POST, endpoint, {
          data: {
            variation,
            details,
            listingAlias,
          },
        });
      },
      getDisclaimers(listingAlias, disclaimerTypes) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getDisclaimers`;

        return requestWrapper(client, POST, endpoint, {
          data: { listingAlias, disclaimerTypes },
        });
      },
      getRentalProtectionProductIntroContent(listingAddressState) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getRentalProtectionProductIntroContent`;

        return requestWrapper(client, GET, endpoint, {
          params: { listingAddressState },
        });
      },
      getRentalProtectionPageV2ForListing(listingAlias, stepOffset) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getRentalProtectionPageV2ForListing`;

        return requestWrapper(client, GET, endpoint, { params: { listingAlias, stepOffset } });
      },
      getRentalProtectionManagePageMultiplePolicies(listingAlias, disclaimerType, stepOffset) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getRentalProtectionManagePageMultiplePolicies`;

        return requestWrapper(client, GET, endpoint, { params: { listingAlias, disclaimerType, stepOffset } });
      },
      storeListingPremiumCoverageSelection({ listingAlias, premiumCoverageId }) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/storeListingPremiumCoverageSelection`;

        return requestWrapper(client, POST, endpoint, {
          data: { listingAlias, premiumCoverageId },
        });
      },
      updatePremiumCoverageSelectionForPolicy({ listingAlias, policyId, premiumCoverageId }) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/updatePremiumCoverageSelectionForPolicy`;

        return requestWrapper(client, POST, endpoint, {
          data: { listingAlias, policyId, premiumCoverageId },
        });
      },
      getCoverageRequestEmbeddedUrlByPolicyId({ policyId }) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getCoverageRequestEmbeddedUrlByPolicyId`;

        return requestWrapper(client, GET, endpoint, { params: { policyId } });
      },
      markCoverageRequestSignedByPolicyId({ policyId, quoteId, documentId }) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/markCoverageRequestSignedByPolicyId`;

        return requestWrapper(client, POST, endpoint, {
          data: { policyId, quoteId, documentId },
        });
      },
      getRentalProtectionProductDetailsContent(listingAlias, listingAddressState) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getRentalProtectionProductDetailsContent`;

        return requestWrapper(client, GET, endpoint, {
          params: { listingAlias, listingAddressState, version: 'v2' },
        });
      },
      dropRentalProtectionPolicy(dataToApi) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/dropRentalProtectionPolicy`;

        return requestWrapper(client, POST, endpoint, dataToApi);
      },
      reinstatePolicy(dataToApi) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/reinstatePolicy`;

        return requestWrapper(client, POST, endpoint, dataToApi);
      },
      storeEffectivePolicyScreenDisplayed(
        params = {
          listingAlias: '',
          userToken: '',
        },
      ) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/storeEffectivePolicyScreenDisplayed`;

        return requestWrapper(client, POST, endpoint, params);
      },
      cancelRentalProtectionPolicy(
        params = {
          policyId: '',
          listingAlias: '',
          userToken: '',
          endCoverageDate: '',
        },
      ) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/cancelRentalProtectionPolicy`;

        return requestWrapper(client, POST, endpoint, params);
      },
      getSecureClaimsUrlForPolicy(
        params = {
          policyId: '',
          listingAlias: '',
        },
      ) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getSecureClaimsUrlForPolicy`;

        return requestWrapper(client, GET, endpoint, { params });
      },
      updateSecurityDepositForListing(propertyId, securityDeposit) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/securityDeposit/update`;
        const params = {
          propertyId,
          securityDeposit,
        };
        return requestWrapper(client, GET, endpoint, { params });
      },
      updateZrmToolsAndSecurityDepositReviewed(listingAlias) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/updateZrmToolsAndSecurityDepositReviewed`;
        const data = {
          listingAlias,
        };
        return requestWrapper(client, POST, endpoint, { data });
      },
      updateOrWaiveSecurityDepositForListing(
        listingAlias,
        securityDeposit,
        waiveSecurityDeposit,
        reviewedListingSecurityDepositForLightweight,
      ) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/securityDeposit/updateOrWaive`;
        const data = {
          listingAlias,
          securityDeposit,
          waiveSecurityDeposit,
          reviewedListingSecurityDepositForLightweight,
        };
        return requestWrapper(client, POST, endpoint, { data });
      },
    },
  },
  users: {
    feedClaims: {
      request(params = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/feedClaims/request`;
        return requestWrapper(client, GET, endpoint, { params });
      },
    },
    applicationPermissions: {
      getApplicationPermissions(userManagementCompanyId) {
        const endpoint = `${RENTAL_MANAGER_API_WEB_V1_PREFIX}/users/userManagementApplicationPermission/getUserApplicationPermissionStatuses`;
        return requestWrapper(client, GET, endpoint, { params: { userManagementCompanyId } });
      },
      createPermissionRequest(userManagementCompanyId) {
        const endpoint = `${RENTAL_MANAGER_API_WEB_V1_PREFIX}/users/userManagementApplicationPermission/createPermissionRequest`;
        return requestWrapper(client, GET, endpoint, { params: { userManagementCompanyId } });
      },
    },
    profile: {
      setName(
        params = {
          firstName: '',
          lastName: '',
        },
      ) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/profile/setName`;

        return requestWrapper(client, GET, endpoint, { params });
      },
      saveUserInfo(
        params = {
          firstName: '',
          lastName: '',
          isLandlord: false,
          isPropertyManager: false,
          propertyCount: 0,
        },
      ) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/profile/saveUserInfo`;

        return requestWrapper(client, GET, endpoint, { params });
      },
    },
    signUrl(url, params = {}) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/multibrandSupport/googleMaps`;
      const { brandName } = params;
      return requestWrapper(client, POST, endpoint, {
        params: { brandName },
        data: { url },
      });
    },

    applications: {
      enableApplications(params = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/applications/enableApplications`;

        return requestWrapper(client, GET, endpoint, { params });
      },
      inviteToApply(data = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/capps/handleApplicationInvitation`;

        return requestWrapper(client, POST, endpoint, { data });
      },
      copyApplicationLinkApi(data = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/capps/handleApplicationInvitationLink`;

        return requestWrapper(client, POST, endpoint, { data });
      },
      getSummary(params = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/summary/applications`;

        return requestWrapper(client, GET, endpoint, { params });
      },
      performIdentityCheck(data = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/idVerification/performIdentityCheck`;

        return requestWrapper(client, POST, endpoint, { data });
      },
      submitIdentityAnswers(data = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/idVerification/submitIdentityAnswers`;

        return requestWrapper(client, POST, endpoint, { data });
      },
      isIdentified() {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/idVerification/isIdentified`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            requestingClient: appName,
          },
        });
      },
      getTermSet() {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/capps/getTermSet`;

        return requestWrapper(client, GET, endpoint, { params: { termSetId: 'landlordScreening' } });
      },
      putTerm(data = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/capps/putTerm`;

        return requestWrapper(client, PUT, endpoint, { data });
      },
    },
    contacts: {
      verifier: {
        confirm(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/contacts/verifier/confirm`;

          return requestWrapper(client, GET, endpoint, {
            params: {
              contact: data.phone,
              contactType: 'phone',
              verificationCode: data.verificationCode,
              verificationSource: data.verificationSource,
            },
          });
        },
        confirmEmail(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/emailVerification/validateVerificationCode`;

          return requestWrapper(client, GET, endpoint, {
            params: {
              listingAlias: data.listingAlias,
              email: data.email,
              verificationCode: data.verificationCode,
            },
          });
        },
        send(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/contacts/verifier/send`;

          return requestWrapper(client, GET, endpoint, {
            params: {
              contact: data.phone,
              contactType: 'phone',
              verificationType: data.verificationType,
              verificationSource: data.verificationSource,
            },
          });
        },
        verified(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/contacts/verifier/verified`;

          return requestWrapper(client, GET, endpoint, {
            params: {
              contact: data.phone,
              contactType: 'phone',
            },
          });
        },
      },
    },
    // Used for retargeting purposes
    trackEvent({ trackingEvent }) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/tracking`;

      return requestWrapper(client, GET, endpoint, { params: { trackingEvent } });
    },
    getUserAddress() {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/profile/getUserAddress`;
      return requestWrapper(client, GET, endpoint);
    },
    setUserAddress(street, unit, city, state, zip) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/profile/setUserAddress`;
      const data = {
        street,
        unit,
        city,
        state,
        zip,
      };
      return requestWrapper(client, POST, endpoint, { data });
    },
    ip(skipClientProxy) {
      const endpoint = 'rental-manager-api/datarouter/ipDetection';

      const request = skipClientProxy ? noProxyClient : client;
      return requestWrapper(request, GET, endpoint);
    },
    intention: {
      insertAction(data) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/intention/insertAction`;

        return requestWrapper(client, POST, endpoint, { data });
      },
    },

    referrals: {
      add(params) {
        const { code, type = 'paidInclusionSales' } = params;
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/referrals/add`;
        let hasError = false;
        let message = null;

        // check for required params
        if (!code) {
          hasError = true;
          message = 'code is required';
        }

        // check if code is alphanumeric
        if (hasError === false && queryUtils.isAlphaNumeric(code) === false) {
          hasError = true;
          message = 'code must be alphanumeric';
        }

        if (hasError) {
          return Promise.reject(
            ErrorRMW({
              message,
              data: params,
            }),
          );
        }

        return requestWrapper(client, GET, endpoint, {
          params: {
            type,
            code,
          },
        });
      },
    },

    reportSpam(params) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/report/spamInquiry`;

      return requestWrapper(client, GET, endpoint, { params });
    },

    // lead
    leads: {
      /**
       * Get all user leads
       *
       * @param data
       * @param {string} data.startDateYyyyMmDd - start date
       * @param {string} data.endDateYyyyMmDd - end date
       *
       * @returns dispatch
       * */
      allLeads(params = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/listingConversation/getLatestConversations`;

        return requestWrapper(client, GET, endpoint, { params });
      },
      lead(data = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/leads/details`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            inquiryId: data.leadId,
            includeListingDetails: data.includeListingDetails,
          },
        });
      },
      // update inquiry status
      updateLeadStatus(data = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/leads/inbox/status`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            inquiryId: data.leadId,
            read: data.read,
          },
        });
      },
      addMessage(params = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/listingConversation/addMessage`;

        return requestWrapper(client, GET, endpoint, { params });
      },
    },
    singleFamilyEnterprise: {
      getContracts() {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/singleFamilyEnterprise/contracts`;
        return requestWrapper(client, GET, endpoint);
      },
      setCustomerId(listingAlias, customerId) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/singleFamilyEnterprise/properties/customerId`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            listingAlias,
            customerId,
          },
        });
      },
    },
    // posting path
    properties: {
      activate(propertyId, daysToExtend) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/activate`;
        // include days to extend in params if it is not null
        const params =
          daysToExtend !== null
            ? {
                propertyId,
                daysToExtend,
              }
            : { propertyId };
        return requestWrapper(client, GET, endpoint, { params });
      },
      isApplicationsAccepted(propertyId, isApplicationsAccepted, requireUserName) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/isApplicationsAccepted`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyId,
            isApplicationsAccepted,
            requireUserName,
          },
        });
      },
      feedListingManagement: {
        getListingSources(params = {}) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/feedListingManagement/getListingSources`;
          return requestWrapper(client, GET, endpoint, { params: { listingAlias: params.listingAlias } });
        },
        createManualVersion(listingAlias) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/feedListingManagement/createManualVersion`;
          return requestWrapper(client, GET, endpoint, { params: { listingAlias } });
        },
      },
      amenities: {
        create(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/amenities/create`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              propertyId: data.propertyId,
              text: data.amenityText,
            },
          });
        },
        delete(amenityId) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/amenities/delete`;
          return requestWrapper(client, GET, endpoint, { params: { amenityId } });
        },
        update(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/amenities/update`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              amenityId: data.amenityId,
              text: data.amenityText,
            },
          });
        },
      },
      apartmentAmenities: {
        // Creates and removes generalized unit amenities.
        create(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/apartmentAmenities/create`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              propertyId: data.propertyId,
              text: data.amenityText,
              availableInSelectUnits: data.availableInSelectUnits,
            },
          });
        },
        delete(amenityId) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/apartmentAmenities/delete`;
          return requestWrapper(client, GET, endpoint, { params: { amenityId } });
        },
        update(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/apartmentAmenities/update`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              amenityId: data.propertyId,
              text: data.amenityText,
            },
          });
        },
      },
      create(data) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/create`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyTypeCode: data.propertyTypeCode,
            listingTypeCode: data.listingTypeCode,
            // Optional
            fullAddress: data.fullAddress,
            unitNumber: data.unitNumber,
            numUnits: data.numUnits,
            customerId: data.customerId,
            includeWebAnalytics: true,
          },
        });
      },
      // accept optional reason param
      // accept optional comment param if reason is `OTHER`
      deactivate(propertyId, reason = null, comment = null) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/deactivate`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyId,
            reason,
            comment,
          },
        });
      },
      deactivateFeedback(feedbackId, reason = null, comment = null) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/deactivate/feedback`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            feedbackId,
            reason,
            comment,
          },
        });
      },
      delete(propertyId, reason) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/delete`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyId,
            reason,
          },
        });
      },
      edit(data) {
        const { fieldName, additionalParams } = data;
        const fieldValue = data[fieldName];

        const params = {};
        params.propertyId = data.propertyId;
        params[fieldName] = fieldValue;

        if (additionalParams) {
          Object.keys(additionalParams).forEach((value) => (params[value] = additionalParams[value]));
        }

        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/${fieldName}`;

        return requestWrapper(client, GET, endpoint, { params });
      },
      // archive
      archive(propertyId) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/archive`;
        return requestWrapper(client, GET, endpoint, { params: { propertyId } });
      },
      // unarchive
      unArchive(propertyId) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/dearchive`;
        return requestWrapper(client, GET, endpoint, { params: { propertyId } });
      },
      vrModel: {
        getAll(encodedAlias) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/vrModel/getAllVrModels`;
          return requestWrapper(client, GET, endpoint, { params: { encodedAlias } });
        },
        associateModel(vrModelId, encodedAlias, shouldAssociate, encodedFloorplanKey) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/vrModel/associateModel`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              vrModelId,
              encodedAlias,
              encodedFloorplanKey,
              shouldAssociate,
            },
          });
        },
        associateListing(vrModelId, encodedAlias, shouldAssociate) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/vrModel/associateListing`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              vrModelId,
              encodedAlias,
              shouldAssociate,
            },
          });
        },
        publishModel(vrModelId, encodedAlias, encodedFloorplanKey) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/vrModel/publishModel`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              encodedAlias,
              vrModelId,
              encodedFloorplanKey,
              shouldPublish: true,
            },
          });
        },
        publishListing(vrModelId, encodedAlias) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/vrModel/publishListing`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              encodedAlias,
              shouldPublish: true,
              vrModelId,
            },
          });
        },
      },
      externalTour: {
        associateModel(externalTour, encodedAlias, shouldAssociate, encodedFloorplanKey) {
          const path = shouldAssociate ? 'addExternalTourForModel' : 'removeExternalTourFromModel';
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/externalTour/${path}`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              encodedAlias,
              encodedFloorplanKey,
              externalTour: {
                ...externalTour,
                tourType: externalTour?.tourType || 'ZRM',
              },
            },
          });
        },
        associateListing(externalTour, encodedAlias, shouldAssociate) {
          const path = shouldAssociate ? 'addExternalTourForListing' : 'removeExternalTourFromListing';
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/externalTour/${path}`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              encodedAlias,
              externalTour,
            },
          });
        },
        publishModel(externalTour, encodedAlias, encodedFloorplanKey) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/externalTour/publishExternalTour`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              encodedAlias,
              encodedFloorplanKey,
              externalTour,
            },
          });
        },
        publishListing(externalTour, encodedAlias) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/externalTour/publishExternalTour`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              encodedAlias,
              externalTour,
            },
          });
        },
      },
      floorPlans: {
        layouts: {
          delete(floorPlanId) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/layouts/delete`;
            return requestWrapper(client, GET, endpoint, { params: { floorPlanId } });
          },
          upload({ formData, floorPlanId } = {}) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/layouts/upload`;
            return requestWrapper(fileClient, POST, endpoint, {
              data: formData,
              params: { floorPlanId },
            });
          },
        },
        photos: {
          caption(data) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/photos/caption`;
            return requestWrapper(client, GET, endpoint, {
              params: {
                photoId: data.photoId,
                caption: data.caption,
              },
            });
          },
          delete(photoId) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/photos/delete`;
            return requestWrapper(client, GET, endpoint, { params: { photoId } });
          },
          upload({ formData, floorPlanId } = {}) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/photos/upload`;
            return requestWrapper(fileClient, POST, endpoint, {
              params: { floorPlanId },
              data: formData,
            });
          },
        },
        units: {
          create(floorPlanId) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/units/create`;
            return requestWrapper(client, GET, endpoint, { params: { floorPlanId } });
          },
          createV2(floorPlanId, params) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/units/createComplete`;
            return requestWrapper(client, GET, endpoint, {
              params: {
                floorPlanId,
                ...params,
              },
            });
          },
          edit(unitId, params) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/units/edit`;
            return requestWrapper(client, GET, endpoint, {
              params: {
                unitId,
                ...params,
              },
            });
          },
        },
        get(floorPlanId) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/details`;
          return requestWrapper(client, GET, endpoint, { params: { floorPlanId } });
        },
        create(propertyId) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/create`;
          return requestWrapper(client, GET, endpoint, { params: { propertyId } });
        },
        edit(data) {
          const { fieldName, floorPlanId } = data;
          const fieldValue = data[fieldName];

          const params = {};
          params.floorPlanId = floorPlanId;
          params[fieldName] = fieldValue;

          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/${fieldName}`;
          return requestWrapper(client, GET, endpoint, { params });
        },
        delete(floorPlanId) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/delete`;
          return requestWrapper(client, GET, endpoint, { params: { floorPlanId } });
        },
      },
      markAcknowledgedLandlordMessage(actionId) {
        const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/rentPayment/markAcknowledged`;
        return requestWrapper(client, POST, endpoint, { data: { actionId } });
      },
      // add boolean to params
      getDetails(propertyId) {
        const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/properties/details`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyId,
            includeTopDupesDetails: true,
            includeVrModels: false,
            includeHasVrModels: false,
            includeWebAnalytics: true,
            includeInappropriatePhotosTag: true,
            includeDeliveryMethod: true,
          },
        });
      },
      getDetailsOverview(listingAlias, includeRestrictions = false) {
        const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/properties/details/overview`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyId: listingAlias,
            includeRestrictions,
          },
        });
      },
      getListingAliasForListingId({ listingId, originalUrl }) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/getListingAliasForListingId`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            listingId,
            originalUrl,
          },
        });
      },
      getRestrictions(listingAlias) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/restrictions/get`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            listingAlias,
          },
        });
      },
      getStatus(propertyId) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/status`;
        return requestWrapper(client, GET, endpoint, { params: { propertyId } });
      },
      feedToManual: {
        statuses() {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/feedToManual/statuses`;
          return requestWrapper(client, GET, endpoint);
        },
        convertAll() {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/feedToManual/convertAll`;
          return requestWrapper(client, POST, endpoint);
        },
        convert(listingAlias) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/feedToManual/convert`;
          return requestWrapper(client, GET, endpoint, { params: { listingAlias } });
        },
      },
      listings(params) {
        /**
         * Get listings for current user
         * @param {boolean} [includeActive=true] - Include active listings
         * @param {boolean} [includeInactive=true] - Include inactive listings
         * @param {string} [searchText=null] - Search text to filter listings
         * @param {string} [startKeyExclusive=null] - start key position
         * @param {int} [limit=20] - number of listings to return
         * @param {boolean} [includeArchived=true] - Include archived listings
         * @param {boolean} [includeUnarchived=false] - Include unarchived listings
         * @param {boolean} [includeAtRisk=false] - Include at-risk listings
         * @param {boolean} [includeFeedClaimAndConvertUpdate=true] - Include Feed claim and convert update info
         * @param {boolean} [includeTours] - Include 3D tours counts
         * */

        const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/properties/list`;

        const listingsParams = ({
          includeActive = Constants.PROPERTIES_ACTIVE_DEFAULT,
          includeInactive = Constants.PROPERTIES_INACTIVE_DEFAULT,
          searchText = Constants.PROPERTIES_SEARCHTEXT_DEFAULT,
          startKeyExclusive = Constants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
          limit = Constants.PROPERTIES_LIMIT_DEFAULT,
          includeArchived = Constants.PROPERTIES_INCLUDE_ARCHIVED,
          includeUnarchived = Constants.PROPERTIES_INCLUDE_UNARCHIVED,
          includeAtRisk = Constants.PROPERTIES_INCLUDE_AT_RISK,
          includeFeedClaimAndConvertUpdate = Constants.PROPERTIES_INCLUDE_FEED_CLAIM_AND_CONVERT_UPDATE,
          includeTours = Constants.PROPERTIES_INCLUDE_TOURS,
          includeContactCount = Constants.PROPERTIES_INCLUDE_CONTACT_COUNT,
          includeUserManagementCompanyId = false,
        } = {}) => ({
          includeActive: Boolean(includeActive),
          includeInactive: Boolean(includeInactive),
          searchText: String(searchText),
          startKeyExclusive: String(startKeyExclusive),
          limit: Number(limit),
          includeFeedClaimAndConvertUpdate: Boolean(includeFeedClaimAndConvertUpdate),
          includeArchived: Boolean(includeArchived),
          includeUnarchived: Boolean(includeUnarchived),
          includeAtRisk: Boolean(includeAtRisk),
          includeTours: Boolean(includeTours),
          includeContactCount: Boolean(includeContactCount),
          includeUserManagementCompanyId: Boolean(includeUserManagementCompanyId),
        });

        return requestWrapper(client, GET, endpoint, {
          params: listingsParams(params),
          timeout: 30000,
        });
      },
      /**
       * Get listings based on property type, location, and brand.
       *
       * @param {string} type - The type of listing to fetch
       * @param {object} params - The query params to pass to the endpoint
       *   @param {boolean} [startKeyExclusive=null] - return response after listing id
       *   @param {boolean} [limit=null] - Limit the number of responses
       *   @param {string} [searchText=''] - Limit the number of responses
       *   @param {boolean} [includeActive] - Include active listings
       *   @param {boolean} [includeInactive] - Include inactive listings
       * */
      listingInt(type, params) {
        // type for listings is statuses
        if (type === SelectPropertyConstants.LISTINGS) {
          type = 'statuses';
        }

        const queryParams = ({
          startKeyExclusive = '',
          limit = 20,
          searchText = '',
          includeActive = true,
          includeInactive = true,
        }) => ({
          startKeyExclusive: String(startKeyExclusive),
          limit: Number(limit),
          searchText: String(searchText),
          includeActive: Boolean(includeActive),
          includeInactive: Boolean(includeInactive),
        });

        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/listings/${type}`;

        return requestWrapper(client, GET, endpoint, { params: queryParams(params) });
      },
      logos: {
        delete(propertyId) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/logos/delete`;
          return requestWrapper(client, GET, endpoint, { params: { propertyId } });
        },
        upload({ formData, propertyId } = {}) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/logos/upload`;

          return requestWrapper(fileClient, POST, endpoint, {
            params: { propertyId },
            data: formData,
          });
        },
      },
      modularListingProgress({ propertyId, step }) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/modularListingProgress`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyId,
            step,
          },
        });
      },
      pets(params) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/pets`;
        return requestWrapper(client, GET, endpoint, { params });
      },
      photos: {
        caption(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/photos/caption`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              photoId: data.photoId,
              caption: data.caption,
            },
          });
        },
        sort(data) {
          const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/properties/photos/rank`;
          return requestWrapper(client, POST, endpoint, {
            params: { propertyId: data.propertyId },
            data: { ranks: data.photosArray },
          });
        },
        delete(photoId) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/photos/delete`;
          return requestWrapper(client, GET, endpoint, { params: { photoId } });
        },
        upload({ formData, propertyId } = {}) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/photos/upload`;
          return requestWrapper(fileClient, POST, endpoint, {
            params: { propertyId },
            data: formData,
          });
        },
      },

      syndication(propertyId) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/syndication`;
        return requestWrapper(client, GET, endpoint, { params: { propertyId } });
      },
      unitAmenities: {
        create(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/unitAmenities/create`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              floorPlanId: data.floorPlanId,
              text: data.amenityText,
              availableInSelectUnits: data.availableInSelectUnits,
            },
          });
        },
        delete(amenityId) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/unitAmenities/delete`;
          return requestWrapper(client, GET, endpoint, { params: { amenityId } });
        },
        update(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/unitAmenities/update`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              amenityId: data.amenityId,
              text: data.amenityText,
            },
          });
        },
      },
      payments: {
        defaultPaymentMethod(listingAlias) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/payments/getDefaultPaymentMethod`;
          return requestWrapper(client, GET, endpoint, { params: { listingId: listingAlias } });
        },
      },
      convertFloorplanOrUnitToNucleus(modelId, unit) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/convert/multiToSingleFamily`;
        return requestWrapper(client, GET, endpoint, { params: { modelId, unit } });
      },
    },
    termsAndConditions: {
      markAcceptedTerms(data = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/termsAndConditions/markAcceptedTerms`;
        return requestWrapper(client, POST, endpoint, { data });
      },
    },
    emailVerification: {
      unverifiedEmailFeedListings(params = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/emailVerification/unverifiedEmailFeedListings`;
        return requestWrapper(client, GET, endpoint, { params });
      },
      sendSecurityToken(params = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/emailVerification/sendSecurityToken`;
        return requestWrapper(client, GET, endpoint, { params });
      },
      verifySecurityToken(securityToken) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/emailVerification/verifySecurityToken`;
        return requestWrapper(client, GET, endpoint, { params: { securityToken } });
      },
    },
    company: {
      getUserManagementProfiles() {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/getUserManagementProfiles`;
        return requestWrapper(client, GET, endpoint);
      },
    },
  },
  marketing: {
    smsAppLead(phoneNumber, campaignId) {
      const endpoint = `${RENTAL_GROWTH_API_V1_PREFIX}/campaign/phone`;
      return requestWrapper(growthClient, GET, endpoint, {
        params: {
          phoneNumber,
          campaignId,
        },
      });
    },
  },
};

export default api;
